import React, { useState } from 'react';
import { Button, makeStyles, Menu, MenuItem } from '@material-ui/core';
import { ITheme } from '../../themes/custom/ITheme';
import {
    bindMenu,
    bindTrigger,
    usePopupState,
} from 'material-ui-popup-state/hooks';
import { IIndexable } from '../GraphicsLibrary/GraphicDetail/GraphicDetail';
import clsx from 'clsx';
import * as actions from '../../store/actions';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import LayersIcon from '@mui/icons-material/Layers';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import NumbersIcon from '@mui/icons-material/Numbers';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { useTranslation } from 'react-i18next';
import theme from './../../themes/GlobalTheme';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { useDispatch } from 'react-redux';
import { ColumnNamesEnum } from '../GraphicsLibrary/Columns';

interface Props {
    admin: boolean;
}

const useStyles = makeStyles((theme: ITheme) => ({
    button: {
        borderRadius: 0,
        margin: 0,
        width: '100%',
        position: 'relative',
        paddingLeft: '40px',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
    left: {
        // borderRadius: '10px  0 0 10px',
    },
    right: {
        // borderRadius: '0 10px 10px 0',
    },
    icon: {
        position: 'absolute',
        left: 10,
    },
    withExpand: {
        paddingRight: '40px',
    },
    menu: {
        marginTop: '43px',
        marginLeft: '1px',
        borderRadius: '0px',
        width: '100%',
    },
    expandIcon: {
        marginLeft: '10px',
        position: 'absolute',
        right: 10,
    },
    editIcon: {
        marginRight: '10px',
        marginLeft: '10px',
    },
    closeIcon: {
        cursor: 'pointer',
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}));

const MainMenu: React.FC<Props> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { admin } = props;
    const dispatch = useDispatch();

    const [isGoToDesignOpen, setIsGoToDesignOpen] = useState(false);
    const [isGoToDesignDetailOpen, setIsGoToDesignDetailOpen] = useState(false);
    const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const addPopupState = usePopupState({
        variant: 'popover',
        popupId: 'addMenu',
    });
    const teamPopupState = usePopupState({
        variant: 'popover',
        popupId: 'teamMenu',
    });

    const myDesignLink = (window as IIndexable)[
        window.MENU_GET_MY_DESIGNS_LINK
    ]();

    const designDetailLink = (window as IIndexable)[
        window.MENU_GET_DESIGN_DETAIL_LINK
    ]();

    return (
        <React.Fragment>
            <ConfirmationDialog
                isOpen={isGoToDesignOpen}
                onClose={() => {
                    setIsGoToDesignOpen(false);
                }}
                onSubmit={() => {
                    window.location.href = myDesignLink;
                }}
            >
                <Typography id="alert-dialog-description">
                    {t(
                        'Do you really want to leave this page? Any changes since the last save will be lost!'
                    )}
                </Typography>
            </ConfirmationDialog>
            <Grid container>
                {!isSmDown && (
                    <React.Fragment>
                        <ConfirmationDialog
                            isOpen={isGoToDesignDetailOpen}
                            onClose={() => {
                                setIsGoToDesignDetailOpen(false);
                            }}
                            onSubmit={() => {
                                window.location.href = designDetailLink;
                            }}
                        >
                            <Typography id="alert-dialog-description">
                                {t(
                                    'Do you really want to leave this page? Any changes since the last save will be lost!'
                                )}
                            </Typography>
                        </ConfirmationDialog>
                        <Grid item xs={12}>
                            <Grid
                                container
                                justifyContent="space-between"
                                mt="10px"
                            >
                                <Grid item>
                                    <Typography variant="h5">
                                        <EditIcon
                                            className={classes.editIcon}
                                        />
                                        {t('Edit your design')}
                                    </Typography>
                                </Grid>
                                <Grid item mr={2}>
                                    <CloseIcon
                                        fontSize="large"
                                        className={classes.closeIcon}
                                        onClick={() => {
                                            setIsGoToDesignDetailOpen(true);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                )}
                <Grid item xs={isXlUp ? 2 : 6}>
                    <Button
                        variant="outlined"
                        {...bindTrigger(addPopupState)}
                        color="secondary"
                        className={clsx(
                            classes.button,
                            classes.left,
                            classes.withExpand
                        )}
                    >
                        <AddIcon className={classes.icon} />
                        <span className={classes.text}>{t('Add')}</span>
                        {!addPopupState.isOpen && (
                            <ExpandMoreIcon className={classes.expandIcon} />
                        )}
                        {addPopupState.isOpen && (
                            <ExpandLessIcon className={classes.expandIcon} />
                        )}
                    </Button>
                    <Menu {...bindMenu(addPopupState)} className={classes.menu}>
                        <MenuItem
                            onClick={() => {
                                (window as IIndexable)[
                                    window.MENU_OPEN_BACKGROUND
                                ]();
                                addPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <WallpaperIcon className={classes.icon} />
                            <span className={classes.text}>
                                {t('Background')}
                            </span>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                (window as IIndexable)[window.MENU_ADD_TEXT]();
                                addPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <TextFieldsIcon className={classes.icon} />
                            <span className={classes.text}>{t('Text')}</span>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                dispatch(
                                    actions.setGraphicsFilter([
                                        {
                                            key: ColumnNamesEnum.myGraphics,
                                            value: true,
                                        },
                                    ])
                                );
                                window.openGraphicsLibrary();
                                addPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <StarBorderIcon className={classes.icon} />
                            <span className={classes.text}>{t('Graphic')}</span>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                dispatch(
                                    actions.setGraphicsFilter([
                                        {
                                            key: ColumnNamesEnum.accessory,
                                            value: true,
                                        },
                                    ])
                                );
                                window.openGraphicsLibrary();
                                addPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <AttachmentIcon className={classes.icon} />
                            <span className={classes.text}>
                                {t('Accessories')}
                            </span>
                        </MenuItem>
                    </Menu>
                </Grid>
                <Grid item xs={isXlUp ? 2.5 : 6}>
                    <Button
                        variant="outlined"
                        {...bindTrigger(teamPopupState)}
                        color="secondary"
                        className={clsx(classes.button, classes.withExpand)}
                    >
                        <PersonIcon className={classes.icon} />
                        <span className={classes.text}>{t('Team')}</span>
                        {!teamPopupState.isOpen && (
                            <ExpandMoreIcon className={classes.expandIcon} />
                        )}
                        {teamPopupState.isOpen && (
                            <ExpandLessIcon className={classes.expandIcon} />
                        )}
                    </Button>
                    <Menu
                        {...bindMenu(teamPopupState)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        className={classes.menu}
                    >
                        <MenuItem
                            onClick={() => {
                                (window as IIndexable)[
                                    window.MENU_ADD_TEXT_TEAM_NAME
                                ]();
                                teamPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <PersonIcon className={classes.icon} />
                            <span className={classes.text}>
                                {t('Add Player Name')}
                            </span>
                        </MenuItem>
                        <MenuItem
                            color="primary"
                            onClick={() => {
                                (window as IIndexable)[
                                    window.MENU_ADD_TEXT_TEAM_NUMBER
                                ]();
                                teamPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <NumbersIcon className={classes.icon} />
                            <span className={classes.text}>
                                {t('Add Player Number')}
                            </span>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                window.openTeamDialog();
                                teamPopupState.close();
                            }}
                            className={classes.button}
                        >
                            <EditIcon className={classes.icon} />
                            <span className={classes.text}>
                                {t('Edit Player List')}
                            </span>
                        </MenuItem>
                    </Menu>
                </Grid>
                <Grid item xs={isXlUp ? 2 : 4}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            (window as IIndexable)[window.MENU_OPEN_LAYERS]();
                        }}
                        color="secondary"
                        className={classes.button}
                    >
                        <LayersIcon className={classes.icon} />
                        <span className={classes.text}>{t('Layers')}</span>
                    </Button>
                </Grid>
                <Grid item xs={isXlUp ? 2.5 : 4}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        className={classes.button}
                        onClick={() => {
                            setIsGoToDesignOpen(true);
                        }}
                        disabled={admin}
                    >
                        <FolderOpenIcon className={classes.icon} />
                        <span className={classes.text}>{t('My designs')}</span>
                    </Button>
                </Grid>
                <Grid item xs={isXlUp ? 3 : 4}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                            window.openChangeStyleDialog();
                        }}
                        disabled={admin}
                        className={clsx(classes.button, classes.right)}
                    >
                        <ContentCutIcon className={classes.icon} />
                        <span className={classes.text}>
                            {t('Change style')}
                        </span>
                    </Button>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default MainMenu;
